import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from './editor/nodes/PlaygroundNodes';
import { processMathEquation } from './latexParser';
import './editor/themes/PlaygroundEditorTheme.css';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const DIFFICULTY_COLORS = [
  'rgba(253, 224, 71, 0.7)',   // Lv1 yellow-300
  'rgba(134, 239, 172, 0.7)',  // Lv2 green-300
  'rgba(147, 197, 253, 0.7)',  // Lv3 blue-300
  'rgba(253, 186, 116, 0.7)',  // Lv4 orange-300
  'rgba(252, 165, 165, 0.7)'   // Lv5 red-300
];

const SimpleProblemPreview = ({ 
  problems = [], 
  onViewProblem,
  onProblemClick,
  highlightedIndex,
  onHighlight,
  accessToken,
  isDesktop = false
}) => {
  const [processedProblems, setProcessedProblems] = useState({});
  const [visibleCards, setVisibleCards] = useState(new Set());
  const scrollContainerRef = useRef(null);
  const cardRefs = useRef({});
  const observerRef = useRef(null);
  const processedIds = useRef(new Set());

  // HTML 엔티티 디코딩 헬퍼 함수
  const decodeHtmlEntities = useCallback((text) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }, []);

  // HTML에서 KaTeX 수식 처리
  const processKaTeXInHtml = useCallback((html) => {
    try {
      // $ ... $ 패턴을 찾아서 KaTeX로 렌더링
      return html.replace(/\$([^$]+)\$/g, (match, equation) => {
        try {
          // HTML 엔티티 디코딩 (예: &gt; → >, &lt; → <)
          const decodedEquation = decodeHtmlEntities(equation);
          const katex = require('katex');
          return katex.renderToString(decodedEquation, {
            throwOnError: false,
            displayMode: false
          });
        } catch (error) {
          console.error('KaTeX 렌더링 오류:', error);
          return match; // 원본 반환
        }
      });
    } catch (error) {
      console.error('KaTeX 처리 오류:', error);
      return html;
    }
  }, [decodeHtmlEntities]);

  // 간단한 텍스트 추출 함수 (fallback)
  const extractSimpleText = useCallback((lexicalData) => {
    try {
      const extractText = (node) => {
        if (!node) return '';
        
        if (node.type === 'text') {
          return node.text || '';
        } else if (node.type === 'equation') {
          return `$${node.equation || ''}$`;
        } else if (node.type === 'paragraph') {
          const text = node.children ? node.children.map(child => extractText(child)).join('') : '';
          return text ? `${text}\n` : '';
        } else if (['layout-container', 'layout-item', 'box', 'box-content', 
                   'sticky', 'tweet', 'youtube', 'poll', 'mention', 'keyword', 
                   'emoji', 'image', 'collapsible-container', 'collapsible-content', 
                   'collapsible-title', 'page-break', 'typeahead'].includes(node.type)) {
          // 특수 노드들은 children만 처리
          return node.children ? node.children.map(child => extractText(child)).join(' ') : '';
        } else if (node.children && Array.isArray(node.children)) {
          return node.children.map(child => extractText(child)).join('');
        }
        return '';
      };

      const text = extractText(lexicalData.root);
      return `<p>${text}</p>`;
    } catch (error) {
      console.error('텍스트 추출 오류:', error);
      return '<p class="text-gray-500">문제 내용을 표시할 수 없습니다.</p>';
    }
  }, []);

  // Lexical 데이터를 HTML로 변환 - equation 노드 특별 처리
  const convertLexicalToHtml = useCallback((lexicalData) => {
    try {
      const editor = createEditor({
        nodes: [...PlaygroundNodes]
      });

      const processNode = (node) => {
        if (!node || typeof node !== 'object') {
          console.warn('processNode: 유효하지 않은 노드:', node);
          return {};
        }
        
        // 지원하지 않는 노드들을 기본 노드로 변환
        if (node.type === 'equation') {
          const equation = node.equation || '';
          try {
            const processedEquation = processMathEquation(equation);
            return {
              type: 'text',
              text: `$${processedEquation}$`,
              mode: 'normal',
              style: '',
              detail: 0,
              format: 0,
              version: 1
            };
          } catch (error) {
            console.error('processMathEquation 오류:', error);
            return {
              type: 'text',
              text: equation,
              mode: 'normal',
              style: '',
              detail: 0,
              format: 0,
              version: 1
            };
          }
        } else if (node.type === 'layout-container') {
          // layout-container를 paragraph로 변환
          const processedChildren = node.children ? node.children.map(child => processNode(child)) : [];
          return {
            type: 'paragraph',
            children: processedChildren,
            direction: 'ltr',
            format: '',
            indent: 0,
            version: 1
          };
        } else if (node.type === 'layout-item') {
          // layout-item을 paragraph로 변환
          const processedChildren = node.children ? node.children.map(child => processNode(child)) : [];
          return {
            type: 'paragraph',
            children: processedChildren,
            direction: 'ltr',
            format: '',
            indent: 0,
            version: 1
          };
        } else if (node.type === 'box') {
          // box를 paragraph로 변환
          const processedChildren = node.children ? node.children.map(child => processNode(child)) : [];
          return {
            type: 'paragraph',
            children: processedChildren,
            direction: 'ltr',
            format: '',
            indent: 0,
            version: 1
          };
        } else if (node.type === 'box-content') {
          // box-content를 paragraph로 변환
          const processedChildren = node.children ? node.children.map(child => processNode(child)) : [];
          return {
            type: 'paragraph',
            children: processedChildren,
            direction: 'ltr',
            format: '',
            indent: 0,
            version: 1
          };
        }
        
        // 알려지지 않은 노드 타입들을 위한 일반적인 처리
        const unsupportedNodeTypes = [
          'sticky', 'tweet', 'youtube', 'poll', 'mention', 'keyword', 
          'emoji', 'image', 'collapsible-container', 'collapsible-content', 
          'collapsible-title', 'page-break', 'typeahead'
        ];
        
        if (unsupportedNodeTypes.includes(node.type)) {
          // 지원하지 않는 노드는 paragraph로 변환
          const processedChildren = node.children ? node.children.map(child => processNode(child)) : [];
          return {
            type: 'paragraph',
            children: processedChildren,
            direction: 'ltr',
            format: '',
            indent: 0,
            version: 1
          };
        }
        
        if (node.children && Array.isArray(node.children)) {
          node.children = node.children.map(child => processNode(child));
        }
        return node;
      };

      const processedData = {
        ...lexicalData,
        root: processNode(lexicalData.root)
      };

      const editorState = editor.parseEditorState(processedData);

      let content = '';
      editor.setEditorState(editorState);
      editor.update(() => {
        content = $generateHtmlFromNodes(editor);
      });

      // KaTeX 수식 처리
      return processKaTeXInHtml(content);
    } catch (error) {
      console.error('Lexical 변환 오류:', error);
      // 실패시 간단한 텍스트 추출 시도
      return extractSimpleText(lexicalData);
    }
  }, [processKaTeXInHtml, extractSimpleText]);

  // 문제 데이터 처리 - useEffect로 안전하게 처리
  useEffect(() => {
    problems.forEach(problem => {
      const pid = String(problem.problem_id);
      
      // 이미 처리된 경우 건너뛰기
      if (processedIds.current.has(pid)) {
        return;
      }

      // 처리 중으로 마킹
      processedIds.current.add(pid);

      try {
        let htmlContent;
        
        if (problem.lexical_data) {
          // lexical 데이터를 HTML로 변환
          htmlContent = convertLexicalToHtml(problem.lexical_data);
        } else if (problem.content) {
          // 일반 content가 있는 경우
          htmlContent = problem.content;
        } else {
          // 콘텐츠가 없는 경우
          htmlContent = '<p class="text-gray-500">문제 내용을 불러올 수 없습니다.</p>';
        }

        setProcessedProblems(prev => ({
          ...prev,
          [pid]: htmlContent
        }));
      } catch (error) {
        console.error(`문제 ${pid} lexical 변환 오류:`, error);
        const errorContent = '<p class="text-red-500">문제를 표시하는데 실패했습니다.</p>';
        
        setProcessedProblems(prev => ({
          ...prev,
          [pid]: errorContent
        }));
      }
    });
  }, [problems, convertLexicalToHtml]);

  // 처리된 콘텐츠 가져오기 함수 (단순 조회만)
  const getProblemContent = useCallback((problem) => {
    const pid = String(problem.problem_id);
    return processedProblems[pid] || '<p class="text-gray-400">처리 중...</p>';
  }, [processedProblems]);

  // IntersectionObserver 설정 - 가시성 추적만
  useEffect(() => {
    const timer = setTimeout(() => {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const problemId = entry.target.getAttribute('data-problem-id');
            if (problemId && entry.isIntersecting) {
              const pid = String(problemId);
              setVisibleCards(prev => new Set([...prev, pid]));
            }
          });
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '200px',
          threshold: 0.01
        }
      );

      const cards = document.querySelectorAll('[data-problem-id]');
      cards.forEach(card => {
        if (observerRef.current) {
          observerRef.current.observe(card);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleCardClick = (index, problem) => {
    if (onHighlight) {
      onHighlight(index, problem);
    }
    if (onProblemClick) {
      onProblemClick(problem);
    }
  };

  const getDifficultyColor = (difficulty) => {
    return DIFFICULTY_COLORS[difficulty - 1] || 'rgba(156, 163, 175, 0.7)';
  };

  if (problems.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-xs text-gray-500">문제 정보가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className={`relative ${isDesktop ? 'h-full -mx-4' : 'h-full -mx-4'}`}>
      <div
        ref={scrollContainerRef}
        className={`problem-preview-cards-container overflow-y-auto overflow-x-hidden ${isDesktop ? 'h-full' : 'h-full'}`}
        style={{
          height: '100%',
          backgroundColor: 'transparent',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <div className="px-4 pt-[8px] pb-4 min-h-full">
          {problems.map((problem, idx) => (
            <div
              key={problem.problem_id}
              data-problem-id={problem.problem_id}
              ref={el => {
                cardRefs.current[idx] = el;
                if (el && observerRef.current) {
                  observerRef.current.observe(el);
                }
              }}
              onClick={() => handleCardClick(idx, problem)}
              className={`relative cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20 ${
                idx === highlightedIndex
                  ? 'border-transparent ring-2 ring-primary'
                  : 'border-gray-200'
              }`}
              style={{
                width: '100%',
                marginBottom: idx === problems.length - 1 ? '14.4px' : '14.4px'
              }}
            >
              {/* 카드 헤더 */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div 
                    className="problem-number text-2xl font-bold"
                    style={{ 
                      color: problem.is_correct === null || problem.is_correct === undefined ? '#000000' :
                             problem.is_correct ? '#4ABC85' : '#F9344B',
                      minWidth: '36px'
                    }}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">PID: {problem.problem_id}</span>
                    {problem.step && (
                      <div 
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                        style={{ backgroundColor: getDifficultyColor(problem.step) }}
                      >
                        Lv.{problem.step}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 문제 미리보기 내용 */}
              <div className="problem-preview-content relative rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 mt-2">
                {(() => {
                  const isVisible = visibleCards.has(String(problem.problem_id));
                  if (!isVisible) {
                    return (
                      <div className="flex h-24 items-center justify-center text-gray-400">
                        <p>스크롤하여 문제 미리보기</p>
                      </div>
                    );
                  }
                  
                  const htmlContent = getProblemContent(problem);
                  return (
                    <div 
                      className="problem-content-preview"
                      style={{
                        fontSize: '12px',
                        lineHeight: '1.5'
                      }}
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 스타일 */}
      <style>{`
        .problem-content-preview *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 400 !important;
          font-size: 12px !important;
          line-height: 1.5 !important;
        }
        .problem-content-preview .katex {
          font-size: 1em !important;
        }
        .problem-content-preview .PlaygroundEditorTheme__boxContent {
          border: 1px solid black;
          padding: 10px;
          margin: 10px auto;
          max-width: 100%;
          border-radius: 4px;
          background-color: #ffffff;
        }
        .problem-content-preview {
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .problem-content-preview img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0 auto;
        }
        .problem-preview-cards-container::-webkit-scrollbar {
          width: 6px;
        }
        .problem-preview-cards-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .problem-preview-cards-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 3px;
          backdrop-filter: blur(10px);
        }
        .problem-preview-cards-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
        
        /* Firefox */
        .problem-preview-cards-container {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
        }
      `}</style>
    </div>
  );
};

export default React.memo(SimpleProblemPreview);
